import { Dialog } from "components/dialog";
import SearchIcon from "components/icons/SearchIcon";
import { ReactNode, useState, useContext } from "react";
import { MdAdd } from "react-icons/md";
import NewFolderOrFile from "views/library/main/components/NewFolderOrFile";
import { useTranslation } from "react-i18next";
import { LibraryContext } from "views/library/context/LibraryContext";

interface SearchAndNewProps {
  value: string;
  onChange: (value: string) => void;
  fetchFiles?: () => Promise<void>;
  className?: string;
  showAdd?: boolean;
  parentId?: string;
  children?: ReactNode;
  header?: ReactNode;
  isLoadingUsage?: boolean;
}

const SearchAndNew = ({
  value,
  onChange,
  className = "",
  showAdd = true,
  parentId = "",
  fetchFiles,
  children,
  header,
  isLoadingUsage: isLoadingUsageProp
}: SearchAndNewProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  // Safely access LibraryContext - works both inside and outside LibraryProvider
  // Note: useContext must be called unconditionally (React Rules of Hooks)
  // Only use isLoadingUsage when showAdd is true (when it's actually needed)
  // Priority: prop > context value > false (default)
  const libraryContext = useContext(LibraryContext);
  const isLoadingUsage = showAdd 
    ? (isLoadingUsageProp ?? libraryContext?.isLoadingUsage ?? false)
    : false;

  return (
    <div
      className={`mx-auto flex w-full flex-col gap-4 overflow-hidden md:!gap-6 ${className}`}
    >
      {header}
      <div className="flex w-full flex-row gap-4 max-w-3xl">
        <div className="flex h-[45px] w-full items-center justify-center rounded-xl border !border-gray-200 dark:!border-none dark:!bg-navy-700">
          <SearchIcon />
          <input
            placeholder={t("library.searchFilesFolders")}
            className="!w-full rounded-xl text-sm font-medium outline-none dark:bg-navy-700 dark:text-white"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
        {showAdd && (
          <Dialog
            setIsOpen={setIsOpen}
            isOpen={isOpen}
            children={
              <NewFolderOrFile
                fetchFiles={fetchFiles}
                parentId={parentId}
                setIsOpen={setIsOpen}
              />
            }
            trigger={
              <button
                disabled={isLoadingUsage}
                className={`linear flex h-[45px] w-full items-center justify-center gap-2 rounded-xl px-4 text-white transition duration-200 md:w-24 ${
                  isLoadingUsage
                    ? "cursor-not-allowed bg-gray-400 opacity-60 dark:bg-gray-600"
                    : "bg-brand-500 hover:bg-brand-600 active:bg-brand-700"
                }`}
              >
                <MdAdd className="text-xl" />
                <span className="flex">{t("common.new")}</span>
              </button>
            }
          />
        )}
        {children}
      </div>
    </div>
  );
};

export default SearchAndNew;
